import { useMemo } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Breadcrumb, Space, Typography } from 'antd';
import type { LeadDetail } from '@viq-admin/screens';
import { space } from '@viq-admin/ui';
import { CompanyLink, ConferenceLink, UserLink } from '../../components/EntityLinks';
import { useAllConferences } from '../../lookups/useLookups';
import { useTrackRecent } from '../../recent/recentItems';
import { LeadDetailBody } from './LeadDetailBody';
import { leadDetailApi } from './leadDetailApi';
import { leadsHref } from './leadsHref';

/**
 * Route wrapper for the super-admin lead detail. Reads the lead id from the
 * route (`/leads/:id`) and hands it to LeadDetailBody, which owns the fetch +
 * rendering. Split so the body can be unit-tested with an explicit id.
 *
 * Above the body sits a compact context header: a breadcrumb back to the
 * Leads list and one linked line, "Company · Rep · Conference", so the
 * operator can jump from a lead to the tenant, the rep who captured it or the
 * event without retyping anything. It reads the SAME query the body does
 * (`['leadDetail', id]` / `leadDetailApi.fetchLeadDetail`), so it costs no
 * extra request and fills in when the body's data arrives.
 *
 * What the detail carries (viq-backend leadDetail handler, mapped by the
 * shared `mapLeadDetail`): the tenant as `person.companyId` + `companyName`,
 * the rep as `capturedByOid` + `capturedByName` ("Unknown rep" when the
 * creator has no profile, "(Inactive)" suffix when deactivated), and the
 * conference as `person.conferenceId` + `conferenceName`. A payload without
 * the id (an older shape) falls back to matching the active-conference lookup
 * by name, and only when exactly one active conference has that name;
 * otherwise the name shows as plain text rather than risk a wrong link.
 */
export function LeadDetailView() {
  const { id = '' } = useParams<{ id: string }>();
  const { data } = useQuery({
    queryKey: ['leadDetail', id],
    queryFn: () => leadDetailApi.fetchLeadDetail(id),
    enabled: !!id,
  });

  useTrackRecent(
    data
      ? {
          kind: 'lead',
          id,
          label: leadLabel(data),
          sublabel: data.companyName || undefined,
        }
      : null,
  );

  return (
    <>
      <div style={{ padding: `${space.lg}px ${space.lg}px 0` }}>
        <Breadcrumb
          items={[
            { title: <Link to={leadsHref()}>Leads</Link> },
            { title: data ? leadLabel(data) : 'Lead' },
          ]}
        />
        {data ? <LeadContextLine detail={data} /> : null}
      </div>
      <LeadDetailBody id={id} />
    </>
  );
}

function leadLabel(detail: LeadDetail): string {
  const p = detail.person;
  return (
    p.displayName ||
    [p.firstName, p.lastName].filter(Boolean).join(' ') ||
    p.email ||
    '(no name)'
  );
}

function LeadContextLine({ detail }: { detail: LeadDetail }) {
  const knownId = detail.person.conferenceId || null;
  const { conferences } = useAllConferences({ enabled: !knownId });
  const conferenceId = useMemo(() => {
    if (knownId) return knownId;
    if (!detail.conferenceName) return null;
    const matches = conferences.filter((c) => c.name === detail.conferenceName);
    return matches.length === 1 ? matches[0].id : null;
  }, [knownId, conferences, detail.conferenceName]);

  const label = (text: string) => <Typography.Text type="secondary">{text} </Typography.Text>;

  return (
    <Space
      wrap
      size={[space.sm, space.xs]}
      split={<Typography.Text type="secondary">·</Typography.Text>}
      style={{ marginTop: space.sm }}
      aria-label="Lead context"
    >
      <span>
        {label('Company')}
        <CompanyLink id={detail.person.companyId || null} name={detail.companyName} />
      </span>
      <span>
        {label('Rep')}
        <UserLink oid={detail.capturedByOid || null} name={detail.capturedByName} />
      </span>
      <span>
        {label('Conference')}
        <ConferenceLink id={conferenceId} name={detail.conferenceName} />
      </span>
    </Space>
  );
}
